"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toDrawing = toDrawing;
exports.range = range;
exports.clampRotation = clampRotation;
exports.roundToMultiple = roundToMultiple;
exports.floorToMultiple = floorToMultiple;
exports.ceilToMultiple = ceilToMultiple;
exports.roundToMaxOutputPositionPrecision = roundToMaxOutputPositionPrecision;
exports.roundToMaxOutputAnglePrecision = roundToMaxOutputAnglePrecision;
exports.roundEpsilonToMultiple = roundEpsilonToMultiple;
exports.getExactPrecisionNumber = getExactPrecisionNumber;
exports.getArcFromVector = getArcFromVector;
exports.transformDrawingByParent = transformDrawingByParent;
exports.areArraysEqualSets = areArraysEqualSets;
exports.rotateDrawing = rotateDrawing;
exports.rotatePoint = rotatePoint;
exports.roundToZero = roundToZero;
exports.translateDrawing = translateDrawing;
exports.translatePoint = translatePoint;
exports.subtractPoints = subtractPoints;
exports.scalePoint = scalePoint;
exports.getArrayDepth = getArrayDepth;
exports.getXyWithMirror = getXyWithMirror;
exports.getLineOffset = getLineOffset;
exports.rejustifyTextUpwards = rejustifyTextUpwards;
exports.normalizeTransformForFootprint = normalizeTransformForFootprint;
exports.getFootprintPinOne = getFootprintPinOne;
exports.calculateFootprintCenter = calculateFootprintCenter;
exports.transformDrawing = transformDrawing;
exports.applyTransformsToDrawing = applyTransformsToDrawing;
const Angles_1 = require("./Angles");
function toDrawing(item) {
    if (item.type === 'zone' && item.polygons.length === 1) {
        const polyBase = item.polygons[0];
        const asPoly = {
            type: 'polygon',
            points: polyBase.points,
            width: polyBase.lineWidth || 0,
            filled: polyBase.filled,
        };
        return asPoly;
    }
    if (item.type === 'track' || item.type === 'via' || item.type === 'zone') {
        return item;
    }
    return undefined;
}
function range(startAt, endAt) {
    return Array.from(new Array(endAt)).filter((l) => l >= startAt);
}
function clampRotation(degrees) {
    let result = degrees;
    while (result < 0) {
        result += 360;
    }
    while (result >= 360) {
        result -= 360;
    }
    if (result === -0) {
        result = 0;
    }
    return result;
}
function roundToMultiple(num, multiple) {
    return Math.round(num / multiple) * multiple;
}
function floorToMultiple(num, multiple) {
    return Math.floor(num / multiple) * multiple;
}
function ceilToMultiple(num, multiple) {
    return Math.ceil(num / multiple) * multiple;
}
function roundToMaxOutputPositionPrecision(num) {
    return getExactPrecisionNumber(roundEpsilonToMultiple(roundToMultiple(num, 0.001), 0.001, 1), 3);
}
function roundToMaxOutputAnglePrecision(num) {
    return getExactPrecisionNumber(roundEpsilonToMultiple(roundToMultiple(num, 0.01), 0.01, 1), 2);
}
function roundEpsilonToMultiple(num, multiple, numEpsilons = 2) {
    const nearestMultiple = Math.round(num / multiple) * multiple;
    if (Math.abs(num - nearestMultiple) < numEpsilons * Number.EPSILON) {
        return nearestMultiple;
    }
    return num;
}
function getExactPrecisionNumber(number, precision) {
    return parseFloat(number.toFixed(precision));
}
function getArcFromVector(start, end, degrees) {
    const angle = degrees * (Math.PI / 180);
    const xi = start[0];
    const yi = start[1];
    const xf = end[0];
    const yf = end[1];
    const radius = (Math.pow(xi - xf, 2) + Math.pow(yi - yf, 2)) / (2 * (xi - xf) * Math.cos(angle) + 2 * (yi - yf) * Math.sin(angle));
    const xc = xi - radius * Math.cos(angle);
    const yc = yi - radius * Math.sin(angle);
    const startAngle = Math.atan2((Math.pow(xi - xf, 2) - Math.pow(yi - yf, 2)) * Math.sin(angle) - 2 * (xi - xf) * (yi - yf) * Math.cos(angle), (Math.pow(yi - yf, 2) - Math.pow(xi - xf, 2)) * Math.cos(angle) - 2 * (xi - xf) * (yi - yf) * Math.sin(angle)) - angle;
    const endAngle = startAngle + angle;
    return {
        center: [xc, yc],
        radius,
        bearings: [startAngle, endAngle],
    };
}
function transformDrawingByParent(drawing, parent) {
    let shape = drawing;
    if (parent.transform.angle) {
        shape = rotateDrawing(shape, -parent.transform.angle);
    }
    if (parent.center) {
        shape = translateDrawing(shape, parent.center);
    }
    else if (parent.pos) {
        shape = translateDrawing(shape, parent.pos);
    }
    return shape;
}
function areArraysEqualSets(a1, a2) {
    const superSet = {};
    for (const i of a1) {
        const e = i + typeof i;
        superSet[e] = 1;
    }
    for (const i of a2) {
        const e = i + typeof i;
        if (!superSet[e]) {
            return false;
        }
        superSet[e] = 2;
    }
    for (const e in superSet) {
        if (superSet[e] === 1) {
            return false;
        }
    }
    return true;
}
function rotateDrawing(_input, degrees) {
    const input = _input;
    switch (input.type) {
        case 'circle':
            return {
                ...input,
                center: rotatePoint(input.center, degrees),
            };
        case 'polygon':
            return {
                ...input,
                pos: input.pos && rotatePoint(input.pos, degrees),
                points: input.points?.map((p) => rotatePoint(p, degrees)),
            };
        case 'rect':
            return {
                ...input,
                start: rotatePoint(input.start, degrees),
                end: rotatePoint(input.end, degrees),
            };
        case 'segment':
            return {
                ...input,
                start: rotatePoint(input.start, degrees),
                end: rotatePoint(input.end, degrees),
            };
        case 'track':
            return {
                ...input,
                start: rotatePoint(input.start, degrees),
                end: rotatePoint(input.end, degrees),
            };
        case 'via':
            return {
                ...input,
                center: rotatePoint(input.center, degrees),
            };
        case 'zone':
            return {
                ...input,
                polygons: input.polygons.map((poly) => ({
                    ...poly,
                    points: poly.points.map((p) => rotatePoint(p, degrees)),
                })),
            };
        case 'pin':
            return {
                ...input,
                start: rotatePoint(input.start, degrees),
                end: rotatePoint(input.end, degrees),
                drawings: input.drawings?.map((d) => ({
                    ...d,
                    drawing: rotateDrawing(d.drawing, degrees),
                })),
            };
        case 'pad':
            return {
                ...input,
                pos: rotatePoint(input.pos, degrees),
                angle: input.angle + degrees,
            };
        case 'text':
            return {
                ...input,
                pos: rotatePoint(input.pos, degrees),
            };
        case 'arc': {
            let startAngle = input.startAngle + degrees;
            let endAngle = input.endAngle + degrees;
            startAngle = clampRotation(startAngle);
            endAngle = clampRotation(endAngle);
            if (startAngle > endAngle) {
                endAngle += 360;
            }
            return {
                ...input,
                center: rotatePoint(input.center, degrees),
                startAngle,
                endAngle,
            };
        }
        case 'curve':
            return {
                ...input,
                start: rotatePoint(input.start, degrees),
                cpa: rotatePoint(input.cpa, degrees),
                cpb: rotatePoint(input.cpb, degrees),
                end: rotatePoint(input.end, degrees),
            };
        case 'image':
        case 'rich-text':
            return {
                ...input,
                pos: rotatePoint(input.pos, degrees),
            };
        default:
            throw new Error(`RotateDrawing was called for input '${input.type}', but it is not implemented.`);
    }
}
function rotatePoint(point, degrees = 0, pivot) {
    if (degrees === 0) {
        return point;
    }
    const pivotX = pivot ? pivot[0] : 0;
    const pivotY = pivot ? pivot[1] : 0;
    const angle = degrees * (Math.PI / 180);
    const rotatedX = Math.cos(angle) * (point[0] - pivotX) - Math.sin(angle) * (point[1] - pivotY) + pivotX;
    const rotatedY = Math.sin(angle) * (point[0] - pivotX) + Math.cos(angle) * (point[1] - pivotY) + pivotY;
    return [rotatedX, rotatedY];
}
function roundToZero(x, epsilonFactor = 1) {
    if (Math.abs(x) <= Number.EPSILON * epsilonFactor) {
        return 0;
    }
    return x;
}
function translateDrawing(_input, offset, mirror) {
    const input = _input;
    switch (input.type) {
        case 'circle':
            return {
                ...input,
                center: translatePoint(input.center, offset, mirror),
            };
        case 'polygon':
            return {
                ...input,
                pos: input.pos && translatePoint(input.pos, offset, mirror),
                points: input.points?.map((p) => translatePoint(p, offset, mirror)),
            };
        case 'rect':
            return {
                ...input,
                start: translatePoint(input.start, offset, mirror),
                end: translatePoint(input.end, offset, mirror),
            };
        case 'segment':
            return {
                ...input,
                start: translatePoint(input.start, offset, mirror),
                end: translatePoint(input.end, offset, mirror),
            };
        case 'track':
            return {
                ...input,
                start: translatePoint(input.start, offset, mirror),
                end: translatePoint(input.end, offset, mirror),
            };
        case 'via':
            return {
                ...input,
                center: translatePoint(input.center, offset, mirror),
            };
        case 'zone':
            return {
                ...input,
                polygons: input.polygons.map((poly) => ({
                    ...poly,
                    points: poly.points.map((p) => translatePoint(p, offset, mirror)),
                })),
            };
        case 'pin':
            return {
                ...input,
                start: translatePoint(input.start, offset, mirror),
                end: translatePoint(input.end, offset, mirror),
                drawings: input.drawings ?
                    input.drawings.map((d) => ({
                        ...d,
                        drawing: translateDrawing(d.drawing, offset, mirror),
                    }))
                    : undefined,
            };
        case 'text':
            return {
                ...input,
                pos: translatePoint(input.pos, offset, mirror),
            };
        case 'arc': {
            let startAngle = input.startAngle;
            let endAngle = input.endAngle;
            if (mirror) {
                if (mirror[1]) {
                    startAngle = 180 - startAngle;
                    endAngle = 180 - endAngle;
                }
                if (mirror[0]) {
                    startAngle = 360 - startAngle;
                    endAngle = 360 - endAngle;
                }
                const numMirrors = (mirror[0] ? 1 : 0) + (mirror[1] ? 1 : 0);
                if (numMirrors % 2 === 1) {
                    [startAngle, endAngle] = [endAngle, startAngle];
                }
                startAngle = clampRotation(startAngle);
                endAngle = clampRotation(endAngle);
                if (startAngle > endAngle) {
                    endAngle += 360;
                }
            }
            return {
                ...input,
                center: translatePoint(input.center, offset, mirror),
                startAngle,
                endAngle,
            };
        }
        case 'pad':
            return {
                ...input,
                pos: translatePoint(input.pos, offset, mirror),
            };
        case 'curve':
            return {
                ...input,
                start: translatePoint(input.start, offset, mirror),
                cpa: translatePoint(input.cpa, offset, mirror),
                cpb: translatePoint(input.cpb, offset, mirror),
                end: translatePoint(input.end, offset, mirror),
            };
        case 'image':
        case 'rich-text':
            return {
                ...input,
                pos: translatePoint(input.pos, offset, mirror),
            };
        default:
            throw Error(`TranslateDrawing was called with '${input.type}', but it is not implemented`);
    }
}
function translatePoint(point, offset, mirror) {
    if (mirror) {
        point = getXyWithMirror(point, mirror);
    }
    return [point[0] + offset[0], point[1] + offset[1]];
}
function subtractPoints(point, offset) {
    return [point[0] - offset[0], point[1] - offset[1]];
}
function scalePoint(point, scaleFactor) {
    if (typeof scaleFactor === 'number') {
        return [point[0] * scaleFactor, point[1] * scaleFactor];
    }
    return [point[0] * scaleFactor[0], point[1] * scaleFactor[1]];
}
function getArrayDepth(value) {
    return Array.isArray(value) ? 1 + Math.max(...value.map(getArrayDepth)) : 0;
}
function getXyWithMirror(point, mirror) {
    return [mirror?.[1] ? -point[0] : point[0], mirror?.[0] ? -point[1] : point[1]];
}
function getLineOffset(length, rotation, start) {
    let end = [length, 0];
    end = rotatePoint(end, rotation);
    end = translatePoint(end, start);
    end = [roundToZero(end[0], 2), roundToZero(end[1], 2)];
    return end;
}
function rejustifyTextUpwards(justify, rot) {
    rot.rot = roundEpsilonToMultiple(clampRotation(rot.rot), 5);
    if (rot.rot > 90 && rot.rot <= 270) {
        return {
            justify: [-justify[0], -justify[1]],
            rot: {
                rot: rot.rot,
                mirrored: [!rot.mirrored[0], !rot.mirrored[1]],
            },
        };
    }
    return { justify, rot };
}
function normalizeTransformForFootprint(layerSet, footprint, compareToFp) {
    const transform = {
        angle: 0,
        mirrored: [false, false],
    };
    if (!footprint || !footprint.pads.length) {
        return transform;
    }
    const center = calculateFootprintCenter(footprint);
    if (Math.abs(center[0]) > Number.EPSILON || Math.abs(center[1]) > Number.EPSILON) {
        transform.pos = [-center[0], -center[1]];
    }
    const pin1 = translateDrawing(getFootprintPinOne(footprint), transform.pos ?? [0, 0]);
    transform.angle = clampRotation(-(0, Angles_1.radiansToDegrees)(Math.atan2(pin1.pos[1], pin1.pos[0])));
    let surfaceLayerChecked = false;
    if (pin1.padType === 'smd') {
        surfaceLayerChecked = true;
        const surfaceLayers = pin1.layers?.map((l) => layerSet.find((ls) => ls.face && ls.id === l)) ?? [];
        if (surfaceLayers.length === 1 && surfaceLayers[0]?.face === 'B') {
            transform.mirrored = [true, true];
        }
    }
    if (!surfaceLayerChecked) {
        if (pin1.padType === 'smd' && compareToFp) {
            const compareToPin1 = getFootprintPinOne(compareToFp);
            const myLayer = pin1.layers?.[0];
            const compareLayer = compareToPin1.layers?.[0];
            if (myLayer !== compareLayer) {
                transform.mirrored = [true, true];
            }
        }
        else if (pin1.padType === 'th' && compareToFp) {
            const mySmdPads = footprint.pads.filter((p) => p.padType === 'smd');
            const myLayer = mySmdPads.reduce((sum, p) => sum + p.layers[0], 0);
            const compareSmdPads = compareToFp.pads.filter((p) => p.padType === 'smd');
            const compareLayer = compareSmdPads.reduce((sum, p) => sum + p.layers[0], 0);
            if (mySmdPads.length === compareSmdPads.length && myLayer !== compareLayer) {
                transform.mirrored = [true, true];
            }
        }
    }
    return transform;
}
function getFootprintPinOne(footprint) {
    const declaredPinOne = footprint.pads.find((p) => !!p.pin1);
    if (declaredPinOne) {
        return declaredPinOne;
    }
    const pins = [...footprint.pads].sort((a, b) => a.ref.localeCompare(b.ref));
    return pins[0];
}
function calculateFootprintCenter(footprint) {
    const center = [0, 0];
    let numPads = 0;
    for (const pad of footprint.pads) {
        if (pad.pos) {
            center[0] += pad.pos[0];
            center[1] += pad.pos[1];
            numPads++;
        }
    }
    if (numPads) {
        center[0] /= numPads;
        center[1] /= numPads;
    }
    return center;
}
function transformDrawing(drawing, transform) {
    let result = drawing;
    result = translateDrawing(result, transform.pos ?? [0, 0], transform.mirrored);
    if (transform.angle) {
        result = rotateDrawing(result, transform.angle);
    }
    return result;
}
function applyTransformsToDrawing(drawing, transforms) {
    return transforms.reduce((d, t) => transformDrawing(d, t), drawing);
}
//# sourceMappingURL=Transform.js.map